import { PERSIST_STORAGE_KEY, PRODUCT_CODES } from './constants.js';
import { deleteCookie, getCookies, setCookie, subscribe as subscribeCookieChange } from './cookie.js';

export { PRODUCT_CODES } from './constants.js';
/**
 * Support persisting cart data in a cookie, shared between checkoutdwa, embedded cart, and consumer.
 */

const ONE_MONTH_IN_DAYS = 30;

const baseDomains = [
    'localhost',
    'bouncie.com',
    'taillight.cloud',
    'taillight.xyz',
    'showcase.dev',
];

export const initialState = {
    isCartPanelOpen: false,
    products: [],
};

// Bridges legacy `locale` with the new `currency`/`language` fields in both
// directions, so callers can migrate independently. Uses Intl.Locale to resolve region
// rather than matching literal strings.
const CURRENCY_BY_REGION = { us: 'usd', ca: 'cad' };

const deriveCurrencyFromLocale = (locale) => {
    if (!locale) {
        return undefined;
    }
    try {
        const region = new Intl.Locale(locale).maximize().region?.toLowerCase();
        return CURRENCY_BY_REGION[region];
    } catch {
        return undefined;
    }
};

const LOCALE_BY_CURRENCY = { usd: 'en-us', cad: 'en-ca' };

const deriveLocaleFromCurrency = (currency) => LOCALE_BY_CURRENCY[currency];

// Whichever of locale/currency `update` actually sets is authoritative and re-derives
// the other — falling back to the persisted value alone would leave it stale once a
// locale-only caller changes it again.
const withDerivedLocaleFields = (data, update = {}) => {
    const currency = update.currency ??
        (update.locale ? deriveCurrencyFromLocale(update.locale) : undefined) ??
        data.currency ??
        deriveCurrencyFromLocale(data.locale);
    const locale = update.locale ??
        (update.currency ? deriveLocaleFromCurrency(update.currency) : undefined) ??
        data.locale ??
        deriveLocaleFromCurrency(currency);
    // Seeds from the derived locale only when no language has ever been set —
    // an explicit language choice must survive a later currency-only update (e.g. changing
    // shipping country), not get overwritten by it.
    const language = update.language ??
        update.locale ??
        data.language ??
        data.locale ??
        locale;
    return {
        ...data,
        ...update,
        ...currency && { currency },
        ...language && { language },
        ...locale && { locale },
    };
};

const serializeCookieValue = (cookie) => JSON.stringify(cookie);

const parseCookieValue = (rawValue) => {
    if (!rawValue) {
        return initialState;
    }
    try {
        const parsed = JSON.parse(rawValue);
        if (Array.isArray(parsed.products)) {
            parsed.products = parsed.products
                // Correct malformed 'bouncie-obd' product codes persisted by older cookies.
                // Value-based (not version-gated) because this cookie has no version field.
                // TODO: it's been two months can we remove this?
                .map((product) =>
                    product.productCode === 'bouncie-obd' ?
                        { ...product, productCode: PRODUCT_CODES.BOUNCIE_OBD } :
                        product,
                )
                // Guard against 0 quantity entries
                .filter((product) => product.quantity > 0);
        } else {
            parsed.products = initialState.products;
        }
        return withDerivedLocaleFields(parsed);
    } catch (err) {
        console.error('Failed to parse persisted cookie value', rawValue, err);
        return initialState;
    }
};

export const getDomain = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    return baseDomains.find((element) => window.location.hostname.includes(element)) || window.location.hostname;
};

export const getPersist = () => {
    const rawCookie = getCookies(PERSIST_STORAGE_KEY);
    return parseCookieValue(rawCookie);
};

export const setPersist = (value) => {
    const options = {
        domain: getDomain(),
        expires: ONE_MONTH_IN_DAYS,
    };
    return setCookie(
        PERSIST_STORAGE_KEY,
        serializeCookieValue(value),
        options,
    );
};

export const updatePersist = (update) => {
    const persistedData = getPersist();
    return setPersist(withDerivedLocaleFields(persistedData, update));
};

export const deletePersist = () => {
    const options = {
        domain: getDomain(),
    };
    deleteCookie(PERSIST_STORAGE_KEY, options);
};

export const subscribe = (handler) => subscribeCookieChange((cookie) => {
    if (cookie.name !== PERSIST_STORAGE_KEY) {
        return;
    }

    handler(parseCookieValue(cookie.value));
});

const sumProductQuantity = (sum, p) => sum + (p.quantity || 0);

export const buildItemKey = (productCode, selection = {}) => `${productCode}|${Object.keys(selection).sort().map((k) => `${k}=${selection[k]}`).join('&')}`;
const byProduct = (productCode, selection) => (item) => buildItemKey(item.productCode, item.selection) === buildItemKey(productCode, selection);

const addNewProductQuantity = (productCode, selection, quantity, products) => [
    ...products,
    { productCode, selection, quantity },
];
const updateProductQuantity = (existingIndex, newQuantity, products) =>
    products.map((p, i) => {
        if (i === existingIndex) {
            return {
                ...p,
                quantity: newQuantity,
            };
        }
        return p;
    });


export const utils = {
    getTotalQuantity: (products = []) => products.reduce(sumProductQuantity, 0),
    getProductQuantity: (productCode, selection, products = []) => {
        const product = products.find(byProduct(productCode, selection));
        return product?.quantity || 0;
    },
    setProductQuantity: (productCode, selection, newQuantity, products = []) => {
        const existingIndex = products.findIndex(byProduct(productCode, selection));

        if (newQuantity <= 0) {
            return existingIndex === -1 ? products : products.filter((_, i) => i !== existingIndex);
        }

        const updatedProducts = existingIndex === -1 ?
            addNewProductQuantity(productCode, selection, newQuantity, products) :
            updateProductQuantity(existingIndex, newQuantity, products);

        return updatedProducts;
    },
    removeProduct: (productCode, selection, products = []) =>
        products.filter((item) => buildItemKey(item.productCode, item.selection) !== buildItemKey(productCode, selection)),
};

/**
 * Remove a product entry from the persisted cart cookie entirely.
 * Distinct from setting quantity to 0 — the entry is deleted, not zeroed.
 * @param {string} productCode
 */
export const removeProduct = (productCode, selection) => {
    const { products: currentProducts = [], ...rest } = getPersist();
    const updatedProducts = utils.removeProduct(productCode, selection, currentProducts);
    setPersist({ ...rest, products: updatedProducts });
};
